import { useEffect, useState } from 'react';
import type { ClaimAttachmentSummary } from '@billing/validation/attachment';
import {
  Alert,
  Box,
  Button,
  Group,
  Image,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconDownload } from '@tabler/icons-react';
import { getAttachmentDownloadURL } from 'api/resource/claimAttachment';
import { useDownloadFromSignedURL } from 'hooks/useDownloadFromSignedURL';
import { getFilenameExtension } from 'utils/fileUtils';

const PREVIEW_MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
};

type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'error' };

export const ClaimAttachmentViewer = ({
  attachment,
  onClose,
}: {
  attachment: ClaimAttachmentSummary;
  onClose: () => void;
}) => {
  const { translate } = useTranslation();
  const [preview, setPreview] = useState<PreviewState>({ status: 'loading' });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const mimeType =
    PREVIEW_MIME_TYPES[getFilenameExtension(attachment.originalFilename)];
  const download = useDownloadFromSignedURL({
    getSignedURL: () => getAttachmentDownloadURL(attachment.id),
    filename: attachment.originalFilename,
    setLoading: setIsDownloading,
    onError: () => setDownloadError(true),
  });

  useEffect(() => {
    if (!mimeType) return;
    const controller = new AbortController();
    let objectURL: string | undefined;
    setPreview({ status: 'loading' });
    const load = async (): Promise<void> => {
      try {
        const signedURL = await getAttachmentDownloadURL(attachment.id);
        if (controller.signal.aborted) return;
        const response = await fetch(signedURL, { signal: controller.signal });
        if (!response.ok) throw new Error('Attachment preview request failed.');
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        // A local blob avoids the download response's attachment disposition.
        objectURL = URL.createObjectURL(new Blob([blob], { type: mimeType }));
        setPreview({ status: 'ready', url: objectURL });
      } catch {
        if (!controller.signal.aborted) setPreview({ status: 'error' });
      }
    };
    void load();
    return () => {
      controller.abort();
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [attachment.id, mimeType]);

  const renderPreview = () => {
    if (!mimeType) {
      return (
        <Text c="dimmed">
          {translate(
            'pages:claims.attachments.previewUnsupported',
            'Preview is not available for this file type. Download the attachment to open it.'
          )}
        </Text>
      );
    }
    if (preview.status === 'loading') {
      return (
        <Group justify="center" py="xl">
          <Loader
            aria-label={translate(
              'pages:claims.attachments.previewLoading',
              'Loading attachment preview'
            )}
          />
        </Group>
      );
    }
    if (preview.status === 'error') {
      return (
        <Alert color="red">
          {translate(
            'pages:claims.attachments.previewError',
            'The attachment could not be previewed. Close the viewer and try again, or download the file.'
          )}
        </Alert>
      );
    }
    if (mimeType === 'application/pdf') {
      return (
        <iframe
          src={preview.url}
          title={attachment.originalFilename}
          style={{ width: '100%', height: '70vh', border: 0 }}
        />
      );
    }
    return (
      <Box mah="70vh" style={{ overflow: 'auto' }}>
        <Image
          src={preview.url}
          alt={attachment.originalFilename}
          fit="contain"
          onError={() => setPreview({ status: 'error' })}
        />
      </Box>
    );
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={attachment.originalFilename}
      size="90%"
      centered
    >
      <Stack>
        <Group justify="flex-end">
          <Button
            type="button"
            variant="light"
            leftSection={<IconDownload size={16} />}
            loading={isDownloading}
            onClick={() => {
              setDownloadError(false);
              void download();
            }}
          >
            {translate('pages:claims.attachments.download', 'Download')}
          </Button>
        </Group>
        {downloadError && (
          <Alert color="red">
            {translate(
              'pages:claims.attachments.downloadError',
              'The attachment could not be downloaded.'
            )}
          </Alert>
        )}
        {renderPreview()}
      </Stack>
    </Modal>
  );
};
